import { Plus, Minus, Settings, ShoppingBag, Info, Leaf, Sprout, WheatOff, LucideIcon } from 'lucide-react';
import { MenuItem } from '@/lib/supabase-types';
import { TranslateFn } from '@/hooks/useTranslation';
import { FanPalette, legibleOn } from '@/lib/fanTheme';
import {
  DietaryTag,
  DIETARY_TAG_LABELS,
  DIETARY_TAG_COLORS,
  dietaryTagKey,
  knownDietaryTags,
  hasItemInfo,
} from '@/lib/dietary';

interface Props {
  item: MenuItem;
  /** Total quantity of this item across every add-on combination in the cart. */
  qty: number;
  t: TranslateFn;
  /** Card, text and accent colours derived from the club's brand colour. */
  palette: FanPalette;
  /** Fires for a card tap and for the Add / + buttons. The caller decides
   *  between opening the add-ons sheet and adding straight to the cart. */
  onAdd: (item: MenuItem) => void;
  onRemove: (item: MenuItem) => void;
  /** Opens the dietary/allergen info sheet. The ⓘ only renders when this is
   *  provided AND the item actually has something to show. */
  onInfo?: (item: MenuItem) => void;
  /** Small pill beside the price (e.g. "Pick-up only" in a 'both' section). */
  badge?: string;
  /** When set, the item can't be ordered here: the card is dimmed and
   *  inert, and this text replaces the Add / quantity control. */
  unavailableLabel?: string;
}

const DIETARY_TAG_ICONS: Record<DietaryTag, LucideIcon> = {
  vegetarian: Leaf,
  vegan: Sprout,
  gluten_free: WheatOff,
};

// Badge hues can't be painted raw: the card colour is computed from the club's
// brand colour, and a fixed "vegan green" disappears on a green-brand club
// (KTP). Walk each hue to legibility on the solid card colour, then build the
// translucent fill + ring from the resolved ink (the LIVE pill pattern).
function DietaryBadges({ item, t, palette }: { item: MenuItem; t: TranslateFn; palette: FanPalette }) {
  const tags = knownDietaryTags(item.dietary_tags);
  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {tags.map(tag => {
        const ink = legibleOn(DIETARY_TAG_COLORS[tag], palette.cardSolid);
        const TagIcon = DIETARY_TAG_ICONS[tag];
        return (
          <span
            key={tag}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
            style={{ color: ink, background: `${ink}17`, boxShadow: `inset 0 0 0 1px ${ink}59` }}
          >
            <TagIcon className="w-3 h-3 flex-shrink-0" />
            {t(dietaryTagKey(tag), { defaultValue: DIETARY_TAG_LABELS[tag] })}
          </span>
        );
      })}
    </div>
  );
}

// Two-row card. Row A is the photo + name; row B is the price + action.
//
// The name used to share one horizontal band with the tile and the Add button,
// which left it ~150px of a 375px screen (~121px once Add became a stepper) —
// about 17 characters a line, so names like "Sipsit – Broadway (Sour Cream and
// Onion)" clipped mid-word even across two lines. Giving the name its own row
// takes it to ~233px, and moving the action out means tapping Add no longer
// reflows the text beside it.
export function MenuItemRow({ item, qty, t, palette, onAdd, onRemove, onInfo, badge, unavailableLabel }: Props) {
  const hasAddons = !!item.addons && item.addons.length > 0;

  return (
    <div
      className={unavailableLabel
        ? 'flex flex-col p-4 rounded-2xl backdrop-blur-md opacity-60'
        : 'flex flex-col p-4 rounded-2xl backdrop-blur-md cursor-pointer transition-transform active:scale-[0.99]'}
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        boxShadow: palette.cardShadow,
      }}
      onClick={unavailableLabel ? undefined : () => onAdd(item)}
    >
      {/* items-start: a three-line name shouldn't drag the tile off the top. */}
      <div className="flex items-start gap-3">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-16 h-16 rounded-xl object-cover shadow-sm flex-shrink-0"
          />
        ) : (
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: palette.tileBg, color: palette.tileInk }}
          >
            <ShoppingBag className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4
            className="font-bold text-base leading-snug break-words line-clamp-3"
            style={{ color: palette.ink }}
          >
            {t(`menu.item.name.${item.id}`, { defaultValue: item.name })}
          </h4>
          {item.size && (
            <p className="text-xs mt-0.5 break-words" style={{ color: palette.inkSoft }}>
              {t(`menu.item.size.${item.id}`, { defaultValue: item.size })}
            </p>
          )}
          <DietaryBadges item={item} t={t} palette={palette} />
        </div>
        {onInfo && hasItemInfo(item) && (
          // stopPropagation for the same reason as the stepper: a fan checking
          // allergens must not put the item in their basket.
          <button
            onClick={(e) => {
              e.stopPropagation();
              onInfo(item);
            }}
            aria-label={t('ordering.itemInfo', { defaultValue: 'Item info' })}
            className="flex-shrink-0 w-8 h-8 -mr-1.5 -mt-1.5 rounded-full flex items-center justify-center transition-transform active:scale-90"
            style={{ color: palette.inkSoft }}
          >
            <Info className="w-[18px] h-[18px]" />
          </button>
        )}
      </div>

      <div
        className="flex items-center justify-between gap-3 mt-3 pt-3"
        style={{ borderTop: `1px solid ${palette.cardBorder}` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="font-semibold text-[15px] tabular-nums flex-shrink-0"
            style={{ color: palette.ink }}
          >
            €{item.price.toFixed(2)}
          </span>
          {hasAddons && (
            <span
              className="text-[11px] flex items-center gap-1 min-w-0"
              style={{ color: palette.inkSoft }}
            >
              <Settings className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                {t('ordering.customizable', { defaultValue: 'Customizable' })}
              </span>
            </span>
          )}
          {badge && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-current whitespace-nowrap flex-shrink-0"
              style={{ color: palette.inkSoft }}
            >
              {badge}
            </span>
          )}
        </div>

        {unavailableLabel ? (
          <span className="text-xs font-semibold text-right" style={{ color: palette.inkSoft }}>
            {unavailableLabel}
          </span>
        ) : qty === 0 ? (
          // No onClick — the tap bubbles to the card, which owns adding.
          <button
            className="flex items-center gap-1 pl-2.5 pr-3.5 py-2 rounded-full text-sm font-bold shadow-md transition-all active:scale-95 flex-shrink-0"
            style={{ background: palette.accent, color: palette.accentInk }}
          >
            <Plus className="w-4 h-4" />
            {t('ordering.add', { defaultValue: 'Add' })}
          </button>
        ) : (
          // stopPropagation is load-bearing: without it "−" also reaches the
          // card's onClick and adds an item instead of removing one.
          <div
            className="flex items-center gap-2 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onRemove(item)}
              aria-label={t('ordering.removeOne', { defaultValue: 'Remove one' })}
              className="w-8 h-8 rounded-full flex items-center justify-center active:scale-90"
              style={{ background: palette.accent, color: palette.accentInk }}
            >
              <Minus className="w-4 h-4" />
            </button>
            {/* key={qty} remounts the span so the bounce replays on every change. */}
            <span
              key={qty}
              className="font-bold text-sm min-w-[20px] text-center tabular-nums animate-bounce-once"
              style={{ color: palette.ink }}
            >
              {qty}
            </span>
            <button
              onClick={() => onAdd(item)}
              aria-label={t('ordering.add', { defaultValue: 'Add' })}
              className="w-8 h-8 rounded-full flex items-center justify-center active:scale-90"
              style={{ background: palette.accent, color: palette.accentInk }}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
